import { z } from "zod";
import { InterfaceKind } from "./common.js";
import { PERMISSIONS } from "./permissions.js";
/** Registry ids are stable, lowercase identifiers referenced by the UI and commands. */
export const InterfaceId = z
    .string()
    .regex(/^[a-z][a-z0-9_.-]{1,63}$/, "lowercase letters, digits, '_', '.', '-' (2-64 chars)");
/** ROS 2 graph name, e.g. /calibration/lidar. */
export const RosName = z.string().regex(/^\/?[A-Za-z_~][A-Za-z0-9_/~{}]*$/, "invalid ROS name");
/** Fully-qualified ROS 2 type, e.g. std_srvs/srv/Trigger. */
export const RosType = z.string().regex(/^[A-Za-z0-9_]+\/(msg|srv|action)\/[A-Za-z0-9_]+$/, "expected pkg/msg|srv|action/Type");
export const QosProfile = z.enum(["default", "sensor_data", "transient_local", "reliable"]);
/**
 * Field bound used by server-side validation before a command leaves the platform
 * (and re-checked by the agent). `path` is a dotted path into the request payload.
 */
export const FieldBound = z.object({
    path: z.string().min(1),
    min: z.number().optional(),
    max: z.number().optional(),
    enum: z.array(z.union([z.string(), z.number()])).optional(),
    required: z.boolean().default(false),
});
/**
 * One allowlisted ROS interface. The registry is the platform's single source of
 * truth for what may be read from / sent to a robot. The onboard agent also keeps
 * a LOCAL allowlist and only binds entries present in both (defence in depth: a
 * compromised platform cannot make the robot call arbitrary services).
 */
export const RegistryEntry = z
    .object({
    id: InterfaceId,
    kind: InterfaceKind,
    rosName: RosName,
    rosType: RosType,
    description: z.string().max(500).default(""),
    /** Permission required to invoke (publish/service/action) or view (subscribe). */
    permission: z.enum(PERMISSIONS),
    /** Physical-effect commands require the caller to hold the robot's control lease. */
    requiresControlLease: z.boolean().default(false),
    /** Ask for explicit user confirmation in the UI before sending. */
    requiresConfirmation: z.boolean().default(false),
    audit: z.boolean().default(true),
    qos: QosProfile.default("default"),
    /** subscribe: max forwarding rate (agent throttles, keeps latest). */
    maxRateHz: z.number().positive().max(50).optional(),
    /** subscribe: platform marks the value stale after this long without an update. */
    staleAfterMs: z.number().int().positive().optional(),
    /** subscribe: optional projection of fields to forward (dotted paths). Empty = whole message. */
    fields: z.array(z.string()).default([]),
    /** service/action: how long the agent waits for a response/result. */
    timeoutSec: z.number().positive().max(4 * 3600).optional(),
    /** action: feedback throttle. */
    feedbackHz: z.number().positive().max(20).optional(),
    /**
     * action: what the agent does with a running goal when the platform link drops.
     * Navigation-type goals should usually cancel; calibration should usually finish.
     */
    onDisconnect: z.enum(["continue", "cancel"]).default("continue"),
    /** Validation applied to request/goal/message payloads. */
    bounds: z.array(FieldBound).default([]),
    /** Optional metric presentation for subscribe entries (Monitoring page). */
    metric: z
        .object({
        label: z.string(),
        /** Dotted path into the message (after projection) that yields the displayed value. */
        valuePath: z.string(),
        unit: z.string().default(""),
        format: z.enum(["number", "percent", "boolean", "text", "duration"]).default("number"),
        precision: z.number().int().min(0).max(6).default(2),
    })
        .optional(),
    enabled: z.boolean().default(true),
})
    .superRefine((e, ctx) => {
    const suffix = e.rosType.split("/")[1];
    const expected = e.kind === "service" ? "srv" : e.kind === "action" ? "action" : "msg";
    if (suffix !== expected) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["rosType"], message: `${e.kind} requires a /${expected}/ type` });
    }
    if (e.kind === "subscribe" && e.requiresControlLease) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["requiresControlLease"], message: "subscriptions cannot require a lease" });
    }
});
/** Snapshot pushed to the robot; `version` is a monotonically increasing integer, `hash` a sha256 of the canonical JSON. */
export const RegistrySnapshot = z.object({
    version: z.number().int().nonnegative(),
    hash: z.string(),
    entries: z.array(RegistryEntry),
});
/**
 * Well-known registry ids the platform itself relies on. Everything else is
 * free-form and added through the admin UI. The robot team must provide ROS
 * endpoints for these (see docs/onboard-requirements.md).
 */
export const WELL_KNOWN_INTERFACES = {
    robotState: "robot.state",
    battery: "robot.battery",
    errors: "robot.errors",
    pose: "robot.pose",
    runWorkflow: "mission.run_workflow",
    pauseMission: "mission.pause",
    resumeMission: "mission.resume",
    teleopCmdVel: "teleop.cmd_vel",
    softwareEstop: "safety.estop",
    softwareEstopReset: "safety.estop_reset",
    /** Configuration activation contract (v0.3 §7.8): transfer + validate + stage, then activate. */
    stageConfigBundle: "config.stage_bundle",
    activateConfigBundle: "config.activate_bundle",
    /** Built-in agent op `map_export` (not a registry entry; must be in the robot's local allowlist). */
    exportMap: "map.export",
    resolveError: "errors.resolve",
    /**
     * Arm controls (Phase 1: observe, named poses, gripper, tool change, stop; Phase 2a: jog, ADR-018).
     * See docs/design-arm-teleop-and-3d-maps.md.
     */
    arm: {
        state: "arm.state",
        goToPose: "arm.go_to_pose",
        gripper: "arm.gripper",
        toolChange: "arm.tool_change",
        stop: "arm.stop",
        /** Publish; carried by the `arm_jog` envelope, never a `cmd` (like teleopCmdVel). */
        jog: "arm.jog",
    },
    calibrate: {
        ft_sensor: "calibration.ft_sensor",
        tool_changer: "calibration.tool_changer",
        wheels: "calibration.wheels",
        camera: "calibration.camera",
        lidar: "calibration.lidar",
        all: "calibration.all",
    },
};
/** Read a dotted path out of a JSON value. Arrays are indexed numerically ("a.0.b"). */
export function getPath(value, path) {
    if (path === "" || path === ".")
        return value;
    let cur = value;
    for (const part of path.split(".")) {
        if (cur === null || cur === undefined)
            return undefined;
        if (Array.isArray(cur)) {
            const idx = Number(part);
            cur = Number.isInteger(idx) ? cur[idx] : undefined;
        }
        else if (typeof cur === "object") {
            cur = cur[part];
        }
        else {
            return undefined;
        }
    }
    return cur;
}
/** Validate a payload against registry bounds. Returns an empty array when valid. */
export function checkBounds(payload, bounds) {
    const out = [];
    for (const b of bounds) {
        const v = getPath(payload, b.path);
        if (v === undefined || v === null) {
            if (b.required)
                out.push({ path: b.path, message: "required" });
            continue;
        }
        if (b.enum && !b.enum.includes(v)) {
            out.push({ path: b.path, message: `must be one of ${b.enum.join(", ")}` });
            continue;
        }
        if (b.min !== undefined || b.max !== undefined) {
            if (typeof v !== "number" || !Number.isFinite(v)) {
                out.push({ path: b.path, message: "must be a finite number" });
                continue;
            }
            if (b.min !== undefined && v < b.min)
                out.push({ path: b.path, message: `must be >= ${b.min}` });
            if (b.max !== undefined && v > b.max)
                out.push({ path: b.path, message: `must be <= ${b.max}` });
        }
    }
    return out;
}
//# sourceMappingURL=registry.js.map